import { lazy } from "react";
import { Route, Navigate } from "react-router-dom";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { MfaPolicyGate } from "@/components/MfaPolicyGate";
import AdminLayout from "@/layouts/AdminLayout";

const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminFacilities = lazy(() => import("@/pages/admin/AdminFacilities"));
const AdminStatutesPage = lazy(() => import("@/pages/admin/AdminStatutesPage"));
const AdminFacilityDetail = lazy(() => import("@/pages/admin/AdminFacilityDetail"));
const AdminDirectory = lazy(() => import("@/pages/admin/AdminDirectory"));
const InmateCrisisProfile = lazy(() => import("@/pages/institution/InmateCrisisProfile"));
const AdminApplicationsPage = lazy(() => import("@/pages/admin/AdminApplicationsPage"));
const AdminContractsPage = lazy(() => import("@/pages/admin/AdminContractsPage"));
const AdminRenewalCenterPage = lazy(() => import("@/pages/admin/AdminRenewalCenterPage"));
const AdminSchedulesPage = lazy(() => import("@/pages/admin/AdminSchedulesPage"));
const AdminAppointmentsPage = lazy(() => import("@/pages/admin/AdminAppointmentsPage"));
const AdminMedicaidPage = lazy(() => import("@/pages/admin/AdminMedicaidPage"));
const AdminClinicalRecordsPage = lazy(() => import("@/pages/admin/AdminClinicalRecordsPage"));
const AdminStandingOrdersPage = lazy(() => import("@/pages/admin/AdminStandingOrdersPage"));
const AdminOnCallPage = lazy(() => import("@/pages/admin/AdminOnCallPage"));
const AdminInmateChartPage = lazy(() => import("@/pages/admin/AdminInmateChartPage"));
const AdminEncounterDetailPage = lazy(() => import("@/pages/admin/AdminEncounterDetailPage"));
const AdminEncounterTemplatesPage = lazy(() => import("@/pages/admin/AdminEncounterTemplatesPage"));
const AdminTemplatesPage = lazy(() => import("@/pages/admin/AdminTemplatesPage"));
const AdminMasterProtocolsPage = lazy(() => import("@/pages/admin/AdminMasterProtocolsPage"));
const AdminScreeningTemplatesPage = lazy(() => import("@/pages/admin/AdminScreeningTemplatesPage"));
const AdminRecordRequestsPage = lazy(() => import("@/pages/admin/AdminRecordRequestsPage"));
const AdminNoShowsPage = lazy(() => import("@/pages/admin/AdminNoShowsPage"));
const AdminPayrollPage = lazy(() => import("@/pages/admin/AdminPayrollPage"));
const AdminAutomationsPage = lazy(() => import("@/pages/admin/AdminAutomationsPage"));
const AutoSchedulingHealthPage = lazy(() => import("@/pages/admin/AutoSchedulingHealthPage"));const AdminFaxPage = lazy(() => import("@/pages/admin/AdminFaxPage"));
const AdminConsentTemplatePage = lazy(() => import("@/pages/admin/AdminConsentTemplatePage"));
const AdminUserConsentsPage = lazy(() => import("@/pages/admin/AdminUserConsentsPage"));
const AdminBillingPage = lazy(() => import("@/pages/admin/AdminBillingPage"));
const AdminFacilityBillingPage = lazy(() => import("@/pages/admin/AdminFacilityBillingPage"));
const AdminPartnersPage = lazy(() => import("@/pages/admin/AdminPartnersPage"));
const AdminFacilityPayoutPage = lazy(() => import("@/pages/admin/AdminFacilityPayoutPage"));
const AdminPreReleasePage = lazy(() => import("@/pages/admin/AdminPreReleasePage"));
const AdminProviderManagementPage = lazy(() => import("@/pages/admin/AdminProviderManagementPage"));
const AdminAuditPage = lazy(() => import("@/pages/admin/AdminAuditPage"));
const MessagesPage = lazy(() => import("@/pages/MessagesPage"));
const SecurityPage = lazy(() => import("@/pages/SecurityPage"));
const AdminCorporateContactPage = lazy(() => import("@/pages/admin/AdminCorporateContactPage"));
const AdminStaffPermissionsPage = lazy(() => import("@/pages/admin/AdminStaffPermissionsPage"));
const AdminRiskAssessmentPage = lazy(() => import("@/pages/institution/RiskAssessmentPage"));
const AdminRecidivismRiskPage = lazy(() => import("@/pages/institution/RecidivismRiskPage"));
const AdminFacilityReportPage = lazy(() => import("@/pages/institution/FacilityReportPage"));
const AdminAppEngagementPage = lazy(() => import("@/pages/admin/AdminAppEngagementPage"));
const AdminPrescriptionSafetyPage = lazy(() => import("@/pages/admin/AdminPrescriptionSafetyPage"));
const AdminAdverseEvents = lazy(() => import("@/pages/admin/AdminAdverseEvents"));
const AdminFlaggedChats = lazy(() => import("@/pages/admin/AdminFlaggedChats"));
const AdminSmsLogPage = lazy(() => import("@/pages/admin/AdminSmsLogPage"));
const AdminSecurityPage = lazy(() => import("@/pages/admin/AdminSecurityPage"));
const AdminCoverageBoardPage = lazy(() => import("@/pages/admin/AdminCoverageBoardPage"));
const AdminEffectivenessPage = lazy(() => import("@/pages/admin/AdminEffectivenessPage"));

export function AdminRoutes() {
    return (
        <Route
            path="/admin"
            element={
                <ProtectedRoute roles={["admin"]}>
                    <MfaPolicyGate><AdminLayout /></MfaPolicyGate>
                </ProtectedRoute>
            }
        >
            <Route index element={<AdminDashboard />} />
            <Route path="facilities" element={<AdminFacilities />} />
            <Route path="statutes" element={<AdminStatutesPage />} />
            <Route path="facilities/:id" element={<AdminFacilityDetail />} />
            <Route path="renewals" element={<AdminRenewalCenterPage />} />
            <Route path="billing" element={<AdminBillingPage />} />
            <Route path="billing/:facilityId" element={<AdminFacilityBillingPage />} />
            <Route path="aging" element={<Navigate to="/admin/billing?tab=ar" replace />} />
            <Route path="partners" element={<AdminPartnersPage />} />
            <Route path="partners/facility/:facilityId" element={<AdminFacilityPayoutPage />} />
            <Route path="audit" element={<AdminAuditPage />} />
            <Route path="medicaid" element={<AdminMedicaidPage />} />
            <Route path="reports" element={<Navigate to="/admin" replace />} />
            <Route path="clinical-records" element={<AdminClinicalRecordsPage />} />
            <Route path="standing-orders" element={<AdminStandingOrdersPage />} />
            <Route path="oncall" element={<AdminOnCallPage />} />
            <Route path="adverse-events" element={<AdminAdverseEvents />} />
            <Route path="record-requests" element={<AdminRecordRequestsPage />} />
            <Route path="no-shows" element={<AdminNoShowsPage />} />
            <Route path="clinical-records/encounter/:id" element={<AdminEncounterDetailPage />} />
            <Route path="clinical-records/:id" element={<AdminInmateChartPage />} />
            <Route path="encounter-templates" element={<AdminEncounterTemplatesPage />} />
            <Route path="templates" element={<AdminTemplatesPage />} />
            <Route path="master-protocols" element={<AdminMasterProtocolsPage />} />
            <Route path="screening-templates" element={<AdminScreeningTemplatesPage />} />
            <Route path="productivity" element={<Navigate to="/admin/provider-management?tab=productivity" replace />} />
            <Route path="coverage" element={<Navigate to="/admin/provider-management?tab=coverage" replace />} />
            <Route path="provider-management" element={<AdminProviderManagementPage />} />
            <Route path="messages" element={<MessagesPage />} />
            <Route path="directory" element={<AdminDirectory />} />
            <Route path="crisis/inmates/:inmateId/crisis-profile" element={<InmateCrisisProfile />} />
            <Route path="sms-log" element={<AdminSmsLogPage />} />
            <Route path="security" element={<AdminSecurityPage />} />
            <Route path="coverage-board" element={<AdminCoverageBoardPage />} />
            <Route path="effectiveness" element={<AdminEffectivenessPage />} />
            <Route path="risk" element={<AdminRiskAssessmentPage admin />} />
            <Route path="recidivism" element={<AdminRecidivismRiskPage admin />} />
            <Route path="facility-report" element={<AdminFacilityReportPage admin />} />
            <Route path="app-engagement" element={<AdminAppEngagementPage />} />
            <Route path="flagged-chats" element={<AdminFlaggedChats />} />
            <Route path="flagged-content" element={<Navigate to="/admin/flagged-chats" replace />} />
            <Route path="applications" element={<AdminApplicationsPage />} />
            <Route path="contracts" element={<AdminContractsPage />} />
            <Route path="credentials" element={<Navigate to="/admin/applications?tab=renewals" replace />} />
            <Route path="schedules" element={<AdminSchedulesPage />} />
            <Route path="appointments" element={<AdminAppointmentsPage />} />
            <Route path="pre-release" element={<AdminPreReleasePage />} />
            <Route path="payroll" element={<AdminPayrollPage />} />
            <Route path="automations" element={<AdminAutomationsPage />} />
            <Route path="auto-scheduling" element={<AutoSchedulingHealthPage />} />
            <Route path="fax" element={<AdminFaxPage />} />
            <Route path="prescription-safety" element={<AdminPrescriptionSafetyPage />} />
            <Route path="consent-template" element={<AdminConsentTemplatePage />} />
            <Route path="user-consents" element={<AdminUserConsentsPage />} />
            <Route path="security" element={<SecurityPage />} />
            <Route path="corporate-contact" element={<AdminCorporateContactPage />} />
            <Route path="staff-permissions" element={<AdminStaffPermissionsPage />} />
            <Route path="caseload-reconcile" element={<Navigate to="/admin/provider-management" replace />} />
            {/* Catch-all: an unrecognized /admin/* path (e.g. one of the removed oversight
                pages) should stay inside the admin shell instead of bouncing to the public
                marketing site. */}
            <Route path="*" element={<Navigate to="/admin" replace />} />
        </Route>
    );
}
